/**
 * Process-wide lock around the @twilio/voice-sdk `Device` singleton.
 *
 * Two call-pipeline implementations live in the codebase: the legacy
 * `useTwilioDialer` hook (mounted via `TelephonyContextProvider` on the
 * `/queue`-class routes) and the new focused-view `TwilioDeviceController`
 * (mounted by `useCallPipeline` on `/focused-view`). They run on disjoint
 * routes today — the focused-view route is rendered without
 * `TelephonyContextProvider`, see `containers/AidaRouter` — so they should
 * never coexist. The risk window is brief: during a hashchange between
 * `/queue` and `/focused-view` both pipelines can be alive for a tick while
 * one is unmounting and the other is mounting.
 *
 * Two Devices instantiated on the same Twilio identity briefly fight each
 * other (Twilio's gateway returns "Already on a call" on the loser, the
 * winner gets the audio) and the SDK leaks an AudioContext on the unowned
 * one until the route transition resolves. This lock makes that exclusion
 * explicit and observable: callers `claim()` before `new Device(...)` and
 * `release()` on `destroy()`. If a different owner currently holds the
 * lock, `claim()` evicts them via the registered evictor — the goal is
 * single-Device-at-any-time, not strict ordering, so newcomer wins.
 *
 * The eviction model preserves both legacy paths:
 *   - Queue → Focused: focused-view claims, evicts legacy. The legacy
 *     `useTwilioDialer` callback runs `deInit()` and resets its hook state.
 *   - Focused → Queue: legacy claims, evicts focused-view. The focused-view
 *     controller runs `deinit()` (which `device.destroy()`s).
 *
 * Tests can call `__resetTwilioDeviceLockForTests()` to clear the global
 * state between cases.
 */

export type TwilioDeviceOwner = 'legacy-telephony-context' | 'focused-view-pipeline';

type LockState = {
	owner: TwilioDeviceOwner;
	evict: () => void;
} | null;

let lock: LockState = null;

/**
 * Claim the global Twilio Device lock for `owner`. If a different owner
 * currently holds the lock, their registered `evict` callback fires
 * synchronously before this returns. The new claim is registered with the
 * supplied `evict` function so a future claimant can do the same.
 *
 * Re-claiming with the same owner is a no-op — useful for callers that
 * may run their init path more than once (e.g. token re-init on 31202)
 * without intending to release the lock.
 */
export function claimTwilioDevice(owner: TwilioDeviceOwner, evict: () => void): void {
	if (lock?.owner === owner) {
		// Same owner re-claiming: refresh the evict callback so the latest
		// instance's teardown runs on a future eviction.
		lock = { owner, evict };
		return;
	}
	if (lock) {
		try {
			lock.evict();
		} catch (err) {
			// Eviction is best-effort — a thrown evictor must not block the
			// new owner from coming online. Log + continue.
			console.error('[twilioDeviceLock] evictor threw during claim', err);
		}
	}
	lock = { owner, evict };
}

/**
 * Release the global Twilio Device lock if held by `owner`. Releases held
 * by a different owner are silently ignored — eviction has already moved
 * the lock past this caller, so the late-running release is a stale
 * callback. Routes that unmount cleanly always call this on teardown.
 */
export function releaseTwilioDevice(owner: TwilioDeviceOwner): void {
	if (lock?.owner === owner) {
		lock = null;
	}
}

/** Returns the current lock owner (or `null` if free). Read-only — does not mutate state. */
export function currentTwilioDeviceOwner(): TwilioDeviceOwner | null {
	return lock?.owner ?? null;
}

/** Test-only escape hatch — wipes the lock without firing the evictor. */
export function __resetTwilioDeviceLockForTests(): void {
	lock = null;
}
